import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters, {
  useSecureQueryParameters,
} from 'hooks/useQueryParameters';
import qs from 'qs';
import { useCallback, useState } from 'react';

export interface PushProvisionAccountData {
  cardArtImageId: string | undefined;
  icCardType: string | undefined;
  logo: string | undefined;
  divisionNumber: number | undefined;
  accountIndex: number | undefined;
}
export interface PushProvisionParams {
  walletId: string;
  passthruFromIdiSdk: string;
  consumerFacingEntityName: string;
  accountData: PushProvisionAccountData;
}

export interface PushProvisionResult {
  passthruToIdiSdk: string;
}

export class PushProvisionError extends Error {}

type PushProvisionHook = () => [
  (params: PushProvisionParams) => Promise<PushProvisionResult>,
  Status,
  boolean,
];
const PATH = '/api/push_provision';

const usePushProvision: PushProvisionHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const {
    cardType: qpCardType,
    overrideKey,
    mockPushProvision,
  } = useQueryParameters();
  const queryParams = useSecureQueryParameters();

  const pushProvision = useCallback(
    async (params: PushProvisionParams) => {
      const {
        accountData: {
          cardArtImageId,
          icCardType,
          logo,
          divisionNumber,
          accountIndex,
        },
      } = params;

      const payload = {
        passthruFromIdiSdk: params.passthruFromIdiSdk,
        walletId: params.walletId,
        consumerFacingEntityName: params.consumerFacingEntityName,
        returnUri: `/push-provision/redirect?${qs.stringify(queryParams)}`,
        mockResponse: mockPushProvision,
        cardArtImageId: cardArtImageId,
        cardType: icCardType || qpCardType,
        overrideKey: overrideKey,
        logo: logo,
        divisionNumber: divisionNumber,
        accountIndex,
      };

      const response = await fetch(PATH, {
        method: 'POST',
        body: payload,
      });

      if (response.ok) {
        const body: PushProvisionResult = await response.json();
        return body;
      } else {
        setError(true);
        return Promise.reject(new PushProvisionError());
      }
    },
    [queryParams, mockPushProvision, qpCardType, overrideKey, fetch],
  );
  return [pushProvision, status, error];
};

export default usePushProvision;
